import dayjs from 'dayjs';
import { Flex, Tag, Tooltip } from 'front-commons/ds';
import { Icon, Typography } from 'hype-ds';
import { useState } from 'react';
import { buildStyles, CircularProgressbar } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';
import { useSpring } from 'react-spring';
import {
	IncentiveCampaignActiveChallengesProps,
	IncentiveCampaignAnimatedValueProps,
	IncentiveCampaignBoosterProductsProps,
	IncentiveCampaignCompletedChallengesProps,
	IncentiveCampaignDatesProps,
	IncentiveCampaignDescriptionProps,
	IncentiveCampaignMandatoryProductsProps,
	IncentiveCampaignProductsProps,
	IncentiveCampaignRootProps,
	IncentiveCampaignTitleProps,
} from './interfaces';

/**
 * Root container of the IncentiveCampaign component.
 *
 * This component provides the structural layout and visual container
 * for the entire campaign card. It applies default padding, border,
 * background color and column layout.
 *
 * It accepts partial `FlexProps` so consumers can override layout behavior
 * (e.g., margin, width, gap adjustments).
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.Root maxWidth="420px">
 *   <IncentiveCampaign.Title>
 *     Summer Sales Campaign
 *   </IncentiveCampaign.Title>
 * </IncentiveCampaign.Root>
 * ```
 */
function Root({ children, ...rest }: IncentiveCampaignRootProps) {
	return (
		<Flex
			padding="1.5rem"
			direction="column"
			border="1px solid"
			borderRadius="8px"
			borderColor="--border-disable"
			backgroundColor="--surface-white"
			{...rest}
		>
			{children}
		</Flex>
	);
}

/**
 * Campaign title section.
 *
 * Renders a medium heading used as the main identifier
 * of the incentive campaign.
 *
 * It inherits all typography props, allowing customization
 * such as alignment, weight, or spacing.
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.Title textAlign="center">
 *   Black Friday Incentive
 * </IncentiveCampaign.Title>
 * ```
 */
function Title({ children, content, slotProps, ...rest }: IncentiveCampaignTitleProps) {
	if (content) {
		return (
			<Tooltip content={content} showDialog={false} {...slotProps?.tooltip}>
				<Flex
					as={Typography.Heading.M}
					color="--text-primary"
					{...rest}
					style={{
						textAlign: 'left',
						// @ts-ignore
						...rest.style,
					}}
				>
					{children}
				</Flex>
			</Tooltip>
		);
	}

	return (
		<Flex as={Typography.Heading.M} color="--text-primary" {...rest}>
			{children}
		</Flex>
	);
}

/**
 * Campaign description section.
 *
 * Displays supporting text explaining campaign rules,
 * mechanics, or rewards.
 *
 * Inherits typography props for visual customization.
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.Description>
 *   Complete challenges and boost your sales performance.
 * </IncentiveCampaign.Description>
 * ```
 */
function Description({ children, content, slotProps, ...rest }: IncentiveCampaignDescriptionProps) {
	return (
		<Tooltip content={content} showDialog={false} {...slotProps?.tooltip}>
			<Flex
				as={Typography.Paragraph.M}
				color="--text-primary"
				{...rest}
				style={{
					textAlign: 'left',
					// @ts-ignore
					...rest.style,
				}}
			>
				{children}
			</Flex>
		</Tooltip>
	);
}

/**
 * Displays campaign validity period.
 *
 * Dates are formatted using `dayjs` in `DD/MM/YYYY` format.
 * It visually communicates when the campaign starts and ends.
 *
 * @param startDate - Campaign start date (ISO string or Date)
 * @param endDate - Campaign end date (ISO string or Date)
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.Dates
 *   startDate="2025-01-01"
 *   endDate="2025-01-31"
 * />
 * ```
 */
function Dates({ endDate, startDate }: IncentiveCampaignDatesProps) {
	return (
		<Flex gap="0.25rem" alignItems="center">
			<Icon color="--text-secondary" name="calendar_month" size="16px" />

			<Typography.Paragraph.S color="--text-secondary">
				Validade{' '}
				<Typography.Paragraph.S
					as="span"
					// @ts-ignore
					style={{ fontWeight: '600' }}
				>
					{dayjs(startDate).format('DD/MM/YYYY')}
				</Typography.Paragraph.S>{' '}
				até{' '}
				<Typography.Paragraph.S
					as="span"
					// @ts-ignore
					style={{ fontWeight: '600' }}
				>
					{dayjs(endDate).format('DD/MM/YYYY')}
				</Typography.Paragraph.S>
			</Typography.Paragraph.S>
		</Flex>
	);
}

/**
 * Displays the percentage of completed challenges.
 *
 * This component renders a circular progress indicator
 * that animates from 0 to the provided percentage.
 *
 * When the value reaches 100%, the typography size changes
 * to emphasize completion.
 *
 * @param children - Completion percentage (number between 0 and 100)
 * @param slotProps.label - Optional typography overrides for the label text
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.CompletedChallenges>
 *   {75}
 * </IncentiveCampaign.CompletedChallenges>
 * ```
 */
export function CompletedChallenges({
	children,
	animate = true,
	immediate = false,
	slotProps,
}: IncentiveCampaignCompletedChallengesProps) {
	const targetValue = Number(children) || 0;
	const [displayPercent, setDisplayPercent] = useState(0);

	useSpring({
		from: { progress: 0 },
		to: { progress: animate ? targetValue : 0 },
		immediate,
		// DURATION CONFIG: Replaces spring physics with a fixed, linear progression time
		config: {
			duration: 1500, // 1.5 seconds total from start to finish
			easing: (t) => t, // Pure linear motion (perfectly consistent speed)
		},
		onChange: ({ value: v }) => {
			setDisplayPercent(Math.floor(v.progress));
		},
	});

	return (
		<Flex gap="0" direction="column" alignItems="center" {...slotProps?.base}>
			<Flex width="72px" height="72px" raw={{ position: 'relative' }}>
				<CircularProgressbar
					strokeWidth={8}
					styles={buildStyles({
						backgroundColor: 'var(--semantic-disable-weak)',
						pathColor: 'var(--green-700)',
						pathTransition: 'none',
					})}
					value={displayPercent}
				/>

				<Flex
					as={targetValue === 100 ? Typography.Heading.XS : Typography.Heading.M}
					justifyContent="center"
					alignItems="center"
					top="50%"
					left="50%"
					transform="translate(-50%, -50%)"
					position="absolute"
					color="--text-primary"
					{...slotProps?.value}
				>
					<span>{`${displayPercent}%`}</span>
				</Flex>
			</Flex>

			<Flex as={Typography.Paragraph.M} color="--text-secondary" {...slotProps?.label}>
				Desafios completos
			</Flex>
		</Flex>
	);
}

/**
 * Animates a numeric value from 0 to the provided value.
 *
 * By default the animation starts immediately when the component mounts.
 * Consumers may disable the initial animation and trigger it later
 * through the `animate` prop.
 */
function AnimatedValue({ value, animate = true, immediate = false, children }: IncentiveCampaignAnimatedValueProps) {
	const [displayValue, setDisplayValue] = useState('0');

	useSpring({
		from: { number: 0 },
		to: { number: animate ? value : 0 },
		immediate,
		config: {
			mass: 1,
			tension: 40,
			friction: 16,
		},
		onChange: ({ value: v }) => {
			setDisplayValue(String(Math.floor(v.number)));
		},
	});

	return children(displayValue);
}

/**
 * Displays the number of active challenges.
 *
 * Used to highlight how many challenges are currently available
 * for the user to complete.
 *
 * @param children - Numeric value representing active challenges
 * @param slotProps - Layout and typography customization options
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.ActiveChallenges>
 *   {3}
 * </IncentiveCampaign.ActiveChallenges>
 * ```
 */
function ActiveChallenges({
	children,
	slotProps,
	animate = true,
	immediate = false,
}: IncentiveCampaignActiveChallengesProps) {
	return (
		<Flex gap="1rem" direction="column" {...slotProps?.base}>
			<Icon color="--semantic-info-base" name="target" />

			<div>
				<AnimatedValue value={Number(children)} animate={animate} immediate={immediate}>
					{(value) => (
						<Flex as={Typography.Heading.M} color="--text-primary" {...slotProps?.value}>
							{value}
						</Flex>
					)}
				</AnimatedValue>

				<Flex as={Typography.Paragraph.M} color="--text-secondary" {...slotProps?.label}>
					Desafios ativos
				</Flex>
			</div>
		</Flex>
	);
}

/**
 * Displays the number of products included in the campaign.
 *
 * Useful to communicate how many products are eligible
 * for participation.
 *
 * @param children - Number of campaign products
 * @param slotProps - Layout and typography overrides
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.Products>
 *   {12}
 * </IncentiveCampaign.Products>
 * ```
 */
function Products({ children, slotProps, animate = true, immediate = false }: IncentiveCampaignProductsProps) {
	return (
		<Flex gap="1rem" direction="column" {...slotProps?.base}>
			{/* @ts-ignore */}
			<Icon color="--semantic-info-base" name="star" />

			<div>
				<AnimatedValue value={Number(children)} animate={animate} immediate={immediate}>
					{(value) => (
						<Flex as={Typography.Heading.M} color="--text-primary" {...slotProps?.value}>
							{value}
						</Flex>
					)}
				</AnimatedValue>

				<Flex as={Typography.Paragraph.M} color="--text-secondary" {...slotProps?.label}>
					Produtos
				</Flex>
			</div>
		</Flex>
	);
}

/**
 * Displays the number of mandatory products required by the campaign.
 *
 * Mandatory products must be included in the participant's sales
 * to satisfy campaign requirements.
 *
 * When `slotProps.label.shorten` is enabled, the label is abbreviated
 * to save horizontal space and a tooltip displays the full text.
 *
 * @param children - Number of mandatory products
 * @param slotProps - Layout and typography customization options
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.MandatoryProducts>
 *   {5}
 * </IncentiveCampaign.MandatoryProducts>
 * ```
 */
function MandatoryProducts({
	children,
	slotProps,
	animate = true,
	immediate = false,
}: IncentiveCampaignMandatoryProductsProps) {
	return (
		<Flex gap="1rem" direction="column" {...slotProps?.base}>
			<Icon color="--semantic-info-base" name="info" />

			<div>
				<AnimatedValue value={Number(children)} animate={animate} immediate={immediate}>
					{(value) => (
						<Flex as={Typography.Heading.M} color="--text-primary" {...slotProps?.value}>
							{value}
						</Flex>
					)}
				</AnimatedValue>

				{slotProps?.label?.shorten ? (
					<Tooltip content="Produtos obrigatórios" showDialog={false}>
						<Flex as={Typography.Paragraph.M} color="--text-secondary" {...slotProps?.label}>
							Prd. obrigatórios
						</Flex>
					</Tooltip>
				) : (
					<Flex as={Typography.Paragraph.M} color="--text-secondary" {...slotProps?.label}>
						Produtos obrigatórios
					</Flex>
				)}
			</div>
		</Flex>
	);
}

/**
 * Displays the number of booster (accelerator) products.
 *
 * Booster products usually provide higher rewards or
 * accelerated campaign progress.
 *
 * @param children - Number of booster products
 * @param slotProps - Layout, typography and Tag customization
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.BoosterProducts>
 *   {4}
 * </IncentiveCampaign.BoosterProducts>
 * ```
 */
function BoosterProducts({
	children,
	slotProps,
	animate = true,
	immediate = false,
}: IncentiveCampaignBoosterProductsProps) {
	return (
		<Flex gap="1rem" direction="column" {...slotProps?.base}>
			<Icon color="--semantic-info-base" name="rocket_launch" />

			<div>
				<AnimatedValue value={Number(children)} animate={animate} immediate={immediate}>
					{(value) => (
						<Flex as={Typography.Heading.M} color="--text-primary" {...slotProps?.value}>
							{value}
						</Flex>
					)}
				</AnimatedValue>

				<Tag bgColor="--yellow-100" color="--yellow-1000" label="Produtos aceleradores" {...slotProps?.tag} />
			</div>
		</Flex>
	);
}

/**
 * Compound component for displaying incentive campaign information.
 *
 * Available components:
 *
 * - Root
 * - Title
 * - Description
 * - Dates
 * - CompletedChallenges
 * - ActiveChallenges
 * - Products
 * - MandatoryProducts
 * - BoosterProducts
 *
 * @example
 * ```tsx
 * <IncentiveCampaign.Root>
 *   <IncentiveCampaign.Title>Summer Campaign</IncentiveCampaign.Title>
 *   <IncentiveCampaign.Description>
 *     Complete challenges to earn rewards.
 *   </IncentiveCampaign.Description>
 *   <IncentiveCampaign.Dates
 *     startDate="2025-01-01"
 *     endDate="2025-01-31"
 *   />
 * </IncentiveCampaign.Root>
 * ```
 */
export const IncentiveCampaign = {
	Root,
	Title,
	Description,
	Dates,
	CompletedChallenges,
	ActiveChallenges,
	Products,
	MandatoryProducts,
	BoosterProducts,
};
